import React, {useState} from "react";
import {
    getFormattedMessage, handleError,
    hasCardExpired,
    isCardAboutToExpire,
    isCardGoingToExpireBeforeDate
} from "../../../helpers/helpers";
import DeletePaymentMethodBtn from "../buttons/DeletePaymentMethodBtn";
import {removePaymentMethods, setPreferredMethod} from "../../../api/requests";
import {PAYMENT_TYPE, RESPONSE_TYPES} from "../../../constants";
import {ServerResponse} from "../../../models/models";

function getDateFromExpDate(expDate) {
    if (!expDate)
        return null;

    const year = `20${expDate.substr(3, 4)}`;
    const month = expDate.substr(0, 2);
    return new Date(year, month - 1);
}

const CardItem = ({
                      paymentMethod,
                      planExpirationDate: planExpirationDateStr,
                      showEditDelete: showEditDelete,
                      setLoading: setLoading,
                      setServerResponse: setServerResponse,
                      allPaymentMethods,
                      setShouldRefresh: setShouldRefresh,
                      awaitingDeleteConfirm: awaitingDeleteConfirm,
                      setAwaitingDeleteConfirm: setAwaitingDeleteConfirm,
                      preferredMethod: preferredMethod,
                      isSelected = false,
                      onClick = () => {
                      },
                      styles = {}
                  }) => {
    const {last4, brand, expDate, country, paymentMethodId,
        preferredForAC, preferredForVRI, preferredForVDS} = paymentMethod || {};
    const showHoverButtons = showEditDelete || false;
    const cardExpDate = getDateFromExpDate(expDate);
    const planExpirationDate = new Date(planExpirationDateStr);

    const handleCancelBtnClick = (e) => {
        e.preventDefault();
        setAwaitingDeleteConfirm(true);
    }

    const handleSetPreferredMethodResult = (result) => {
        if (result.status !== 200) {
            console.error('Failed to save payment data');
            handleError(result, i18n.payments.error.genericServerError, setServerResponse);
            return;
        } else if (result.data.error !== null && result.data.error !== undefined) {
            setServerResponse(new ServerResponse(RESPONSE_TYPES.error, i18n.payments.error.genericTitle,
                result.data.error));
            return;
        }

        setShouldRefresh(val => !val);
    };

    const handleConfirmedCancelBtnClick = (e) => {
        e.preventDefault();
        if (!paymentMethodId) {
            return;
        }

        setLoading(true);
        removePaymentMethods(paymentMethodId, (result) => {
            if ((preferredForAC || preferredForVRI || preferredForVDS) && allPaymentMethods.length - 1 > 0) {
                for (let i = 0; i < allPaymentMethods.length; i++) {
                    const paymentID = allPaymentMethods[i].paymentMethodId;
                    if (paymentID && paymentID !== paymentMethodId) {
                        if (preferredForAC)
                            setPreferredMethod(PAYMENT_TYPE.AC, paymentID, handleSetPreferredMethodResult);
                        if (preferredForVRI)
                            setPreferredMethod(PAYMENT_TYPE.VRI, paymentID, handleSetPreferredMethodResult);
                        if (preferredForVDS)
                            setPreferredMethod(PAYMENT_TYPE.VDS, paymentID, handleSetPreferredMethodResult);

                        sleep(2000).then(r => {
                            setLoading(false);
                            setAwaitingDeleteConfirm(false);
                        });

                        break;
                    }
                }
            } else {
                if (result.status !== 200) {
                    setLoading(false);
                    console.error('Failed to remove payment methods');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }

                setShouldRefresh(val => !val);
                setLoading(true);
                sleep(2000).then(r => {
                    setLoading(false);
                    setAwaitingDeleteConfirm(false);
                });
            }
        });
    };

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const getCardHeaderText = () => {
        const suffix = '';

        if (hasCardExpired(cardExpDate)) {
            return `${i18n.payments.expired}${suffix}`;
        } else if (isCardAboutToExpire(cardExpDate)) {
            return `${i18n.payments.aboutToExpire}${suffix}`;
        } else if (isCardGoingToExpireBeforeDate(cardExpDate, planExpirationDate)) {
            return `${i18n.payments.willExpireBeforeRenewal}${suffix}`;
        }
        return `${i18n.payments.valid}${suffix}`;
    }

    const getCardDivCss = () => {
        let css = '';

        if (hasCardExpired(cardExpDate)) {
            css += 'lightred ';
        } else if (isCardAboutToExpire(cardExpDate) || isCardGoingToExpireBeforeDate(cardExpDate, planExpirationDate)) {
            css += 'lightyellow ';
        }

        if (onClick) {
            css += 'clickable ';
        }

        if (isSelected) {
            css += 'selected-credit-card ';
        }

        return css;
    }

    const getCardImgSrc = () => {
        if (hasCardExpired(cardExpDate)
            || isCardAboutToExpire(cardExpDate)
            || isCardGoingToExpireBeforeDate(cardExpDate, planExpirationDate)) {
            return '../images/creditAlert.png';
        }
        return '../images/creditOk.png';
    };

    const getBrand = () => {
        if (brand === 'visa') {
            return (
                <img alt={`visa`}
                     className={'brand-img'}
                     src={'../images/brandVisa.png'}/>
            );
        } else if (brand === 'mastercard') {
            return (
                <img alt={`mastercard`}
                     className={'brand-img'}
                     src={'../images/brandMastercard.png'}/>
            );
        } else if (brand === 'discover') {
            return (
                <img alt={`discover`}
                     className={'brand-img'}
                     src={'../images/brandDiscover.png'}/>
            );
        } else if (brand === 'amex') {
            return (
                <img alt={`american-express`}
                     className={'brand-img'}
                     src={'../images/brandAmex.png'}/>
            );
        }

        return (
            <div className={`plan-sub-details`}>{getFormattedMessage(i18n.payments.brandDetail, brand)}</div>
        );
    }

    if (!paymentMethod)
        return <></>;

    return (
        <div className={`plan-item credit-card-div ${getCardDivCss()}`}
             onClick={onClick}
             data-testid={'cardItem'}
             data-payment-method-id={paymentMethodId}
             data-is-selected={isSelected}
             data-preferred-for-ac={preferredForAC}
             data-preferred-for-vri={preferredForVRI}
             data-preferred-for-vds={preferredForVDS}
             style={styles}>
            {
                !awaitingDeleteConfirm && (
                    <div>
                        {
                            preferredMethod && (
                                <div style={{display: "grid", gridAutoFlow: "column"}} data-testid={'preferredMethodDiv'}>
                                    <div className={'plan-sub-title'}>
                                        {i18n.payments.preferredPaymentMethod}
                                    </div>
                                    <img className="clear_button_small_svg"
                                         style={{cursor: "pointer"}}
                                         src={'../images/svg/alert-info.svg'}
                                         title={i18n.payments.preferredPaymentMethodDetails}
                                         alt=""/>
                                </div>)
                        }
                        <div style={{display: "flex"}}>
                            <div className={'flexColumn'}>
                                <img alt={"Card"}
                                     data-testid={'creditCardImg'}
                                     className={`credit-card-img`}
                                     src={getCardImgSrc()}/>
                                {getBrand()}
                            </div>
                            <div className={"credit-card-text"} data-testid={'creditCardText'}>
                                <div className={`plan-sub-title`}>{getCardHeaderText()}</div>
                                <div
                                    className={`plan-sub-details`}>{getFormattedMessage(i18n.payments.endingInDetail, last4)}</div>
                                <div
                                    className={`plan-sub-details`}>{getFormattedMessage(i18n.payments.expirationDetail, expDate)}</div>
                                <div
                                    className={`plan-sub-details`}>{getFormattedMessage(i18n.payments.countryDetail, country)}</div>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                showHoverButtons && (
                    <DeletePaymentMethodBtn confirm={awaitingDeleteConfirm}
                                            handleCancelBtnClick={handleCancelBtnClick}
                                            handleConfirmedCancelBtnClick={handleConfirmedCancelBtnClick}
                                            setConfirm={setAwaitingDeleteConfirm}/>
                )
            }
        </div>
    );
}

export default CardItem;